
// src/features/authentication/components/LoginForm.js
import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Checkbox,
    FormControlLabel,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';

import useField from '../../../hooks/useField';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { useLoginMutation } from '../authApiSlice';
import ProgressLoader from '../../../components/ProgressLoader';
import { useAddNewLokiMutation } from '../../../api/lokiSlice';

const LoginForm = ({ message }) => { // Add message prop
    const usernameRef = useRef();
    const passwordRef = useRef();
    const [errorMsg, setErrorMsg] = useState('');
    const [sessionExpiredMsg, setSessionExpiredMsg] = useState(message || ''); // State for expiration message
    const [autoLogoutMsg, setAutoLogoutMsg] = useState(''); // State for auto-logout message
    const [showPassword, setShowPassword] = useState(false); // State for password visibility

    const navigate = useNavigate();

    const [login, { isLoading }] = useLoginMutation();
    const [sendLog] = useAddNewLokiMutation();

    const { setValue: setUsername, ...username } = useField('text');
    const { setValue: setPassword, ...password } = useField('password');
    const [rememberUser, setRememberUser] = useLocalStorage('rememberMe', false);

    // Check for auto-logout and pre-fill username on mount and when page becomes visible
    useEffect(() => {
        const checkAutoLogout = () => {
            // Check if user was auto-logged out
            const wasAutoLoggedOut = localStorage.getItem('autoLoggedOut');
            if (wasAutoLoggedOut === 'true') {
                setAutoLogoutMsg('Sinut kirjattiin ulos automaattisesti (2 tuntia epäaktiivisena).');
            }

            // Pre-fill username if it was saved AND rememberUser is currently true
            const savedUsername = localStorage.getItem('savedUsername');
            const currentRememberMe = localStorage.getItem('rememberMe');
            if (savedUsername && currentRememberMe === 'true') {
                setUsername(savedUsername);
                // Focus password field if username is pre-filled
                setTimeout(() => passwordRef.current?.focus(), 100);
            } else {
                // Clear saved username if rememberUser is false
                if (currentRememberMe !== 'true') {
                    localStorage.removeItem('savedUsername');
                }
                // Focus username field if not pre-filled
                setTimeout(() => usernameRef.current?.focus(), 100);
            }
        };

        // Run on mount
        checkAutoLogout();

        // Also run when page becomes visible (user switches back to tab)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                const wasAutoLoggedOut = localStorage.getItem('autoLoggedOut');
                if (wasAutoLoggedOut === 'true' && !autoLogoutMsg) {
                    setAutoLogoutMsg('Sinut kirjattiin ulos automaattisesti (2 tuntia epäaktiivisena).');
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [setUsername, autoLogoutMsg]);

    // Clear error message when typing starts
    useEffect(() => {
        setErrorMsg('');
        // Don't clear auto-logout message - let user dismiss it manually
    }, [username.value, password.value]);

    // Display the session expired message when it’s passed via props
    useEffect(() => {
        if (message) {
            setSessionExpiredMsg(message);
            // Optionally clear it after a timeout
            const timer = setTimeout(() => setSessionExpiredMsg(''), 5000); // Hide after 5 seconds
            return () => clearTimeout(timer);
        }
    }, [message]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        const credentials = {
            username: username.value.toLowerCase(),
            password: password.value
        };

        try {
            const result = await login(credentials).unwrap();
            await sendLog({
                name: result.name,
                action: 'kirjautui sisään'
            });

            // Clear auto-logout flags on successful login
            localStorage.removeItem('autoLoggedOut');
            localStorage.removeItem('autoLogoutTime');
            // Keep savedUsername for next time if rememberUser is still true

            setUsername('');
            setPassword('');
            navigate('/');
        } catch (error) {
            if (error.status === 400)
                setErrorMsg('Käyttäjätunnus tai salasana puuttuvat');
            else if (error.status === 401)
                setErrorMsg('Väärä salasana tai käyttäjätunnus');
            else if (!error.status || error.status === 500)
                setErrorMsg('Ei vastausta palvelimelta. Ota yhteyttä IT-tukeen.');
            else
                setErrorMsg(error.data?.message);
        }
    };

    if (isLoading)
        return <ProgressLoader msg={'Kirjaudutaan sisään..'} />;

    return (
        <form onSubmit={handleSubmit}>
            {/* Display auto-logout message if present */}
            {autoLogoutMsg && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setAutoLogoutMsg('');
                        localStorage.removeItem('autoLoggedOut');
                        localStorage.removeItem('autoLogoutTime');
                    }}
                >
                    {autoLogoutMsg}
                </Alert>
            )}
            {/* Display session expiration message if present */}
            {sessionExpiredMsg && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {sessionExpiredMsg}
                </Alert>
            )}
            {/* Display error message if present */}
            {errorMsg && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMsg}
                </Alert>
            )}
            <Box
                display="flex"
                flexDirection="column"
                maxWidth={500}
                justifyContent="center"
                margin="auto"
                mt={5}
                p={{ xs: 2, sm: 7, md: 10 }}
                borderRadius={5}
                boxShadow="5px 5px 10px #ccc"
                sx={{ ':hover': { boxShadow: '10px 10px 20px #ccc' } }}
            >
                <Typography
                    variant="h4"
                    padding={3}
                    align="center"
                    sx={{ display: 'block', fontSize: '1.5rem' }}
                >
                    Kirjaudu sisään
                </Typography>
                <TextField
                    inputRef={usernameRef}
                    margin="normal"
                    variant="outlined"
                    placeholder="Käyttäjätunnus tai sähköpostiosoite"
                    required
                    {...username}
                    onKeyDown={(e) => {
                        if (username.length && e.key === 'Enter')
                            passwordRef.current.focus();
                    }}
                />
                <TextField
                    inputRef={passwordRef}
                    FormHelperTextProps={{ fontFamily: '"Bree Serif", sans-serif' }}
                    margin="normal"
                    variant="outlined"
                    placeholder="Salasana"
                    required
                    {...password}
                    type={showPassword ? 'text' : 'password'}
                    onKeyDown={(e) => {
                        if (password.length && username.length && e.key === 'Enter')
                            handleSubmit(e);
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword(!showPassword)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <Button
                    sx={{
                        marginTop: 3,
                        borderRadius: 3,
                        color: 'white',
                        '&:hover': {
                            bgcolor: 'taphaBlue.main',
                            color: 'white',
                            transition: '0.2s'
                        },
                        fontFamily: '"Bree Serif"',
                        letterSpacing: '1px'
                    }}
                    variant="contained"
                    color="taphaGreen"
                    size="medium"
                    type="submit"
                >
                    Kirjaudu sisään
                </Button>
                <Box display="flex" justifyContent="center" marginBottom={0} marginTop={3}>
                    <FormControlLabel
                        label={
                            <Typography
                                sx={{ fontFamily: '"Bree Serif", serif' }}
                                variant="body1"
                            >
                                Muista minut
                            </Typography>
                        }
                        control={
                            <Checkbox
                                name="Muista minut"
                                value="Muista minut"
                                checked={rememberUser}
                                onChange={() => setRememberUser(!rememberUser)}
                                inputProps={{ 'aria-label': 'controlled' }}
                                color="taphaBlue"
                            />
                        }
                    />
                </Box>
            </Box>
        </form>
    );
};

export default LoginForm;